import React, { useEffect, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { useSelector } from 'react-redux';
import { addressToString, Entity } from '@debbie/common/dist/index.js';
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { DEFAULT_FILE_NAME, EditorToolbarOptions, } from '@debbie/common/dist/constants.js';
import { getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { getMentionSuggestions } from '@debbie/common/dist/templates/suggestions.js';
import { cancelDebounce, debounce } from '@debbie/common/dist/util/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import * as templatesAPI from '@debbie/cortex/dist/api/templates.js';
import { APIBASE } from '@debbie/cortex/dist/config.js';
import { selectLetter } from '@debbie/cortex/dist/newInteractions/selectors.js';
import { Button, Checkbox, Input, Intent, Modal } from '..//index.js';
import { parseText } from '../../utils/templates.js';
import Info from '../Icons/Info.js';
import LetterPreview from './LetterPreview.js';
import MentionExplanation from './MentionExplanation.js';
import TemplateSearch from './TemplateSearch.js';
import { toHtml } from './utils.js';
import { downloadFile } from '@debbie/cortex/dist/files.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { filterPropertyTypes } from '@debbie/common/dist/properties/index.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
const LetterInteraction = (props) => {
    const singleCase = props.case;
    const lang = useSelector((s) => s.lang);
    const letter = useSelector((s) => selectLetter(s, singleCase.caseId));
    const tenant = useSelector(selectTenant);
    const propertyTypesLoadable = usePropertyTypes();
    const propertyTypes = h.hasData(propertyTypesLoadable)
        ? propertyTypesLoadable.data
        : undefined;
    const [reset, setReset] = useState(false);
    const [content, setContent] = useState(letter.editorState);
    useEffect(() => {
        if (reset) {
            setContent(letter.editorState);
            setReset(false);
        }
    }, [letter.editorState]);
    const [letterPreviewModalOpen, setLetterPreviewModalOpen] = useState(false);
    const [mentionExplanationModalOpen, setMentionExplanationModalOpen] = useState(false);
    const [visibleToClients, setVisibleToClients] = useState(true);
    const creditorLoadable = useCreditor(props.case?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const collectorLoadable = useCollector((props.case &&
        creditor &&
        getCollectorOnCase(props.case.type, props.case.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId)) ||
        undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const contactPerson = getContactPerson(props.customer);
    const letterTo = contactPerson && contactPerson.details.address && contactPerson.details.name
        ? contactPerson.details.name
        : '';
    const letterAddress = contactPerson && contactPerson.details.address
        ? contactPerson.details.address
        : null;
    const toggleLetterPreviewModal = () => {
        setLetterPreviewModalOpen(!letterPreviewModalOpen);
    };
    const toggleMentionExplanation = () => {
        setMentionExplanationModalOpen(!mentionExplanationModalOpen);
    };
    const mentionSuggestions = getMentionSuggestions(lang, TemplateType.LETTER, propertyTypes
        ? filterPropertyTypes(propertyTypes, Entity.Case, 'case_groups', singleCase.caseGroup?.id || null)
        : undefined, tenant);
    const sendLetter = () => {
        try {
            if (!letterAddress)
                return alert('No address provided');
            if (!collector)
                return alert('Could not find collector');
            const pdfBody = parseText(toHtml(content), mentionSuggestions);
            cancelDebounce('letter-content');
            setReset(true);
            const letterBody = toHtml(content);
            c.newInteractions.sendLetter(singleCase.caseId, Entity.Case, letter.templateId, letterBody, pdfBody, collector, visibleToClients ? 'client' : 'caseworker', letterAddress, letter.intent);
        }
        catch (error) {
            console.error(error);
            alert('Error');
        }
    };
    const hasBody = () => {
        return !!(content &&
            content.getCurrentContent() &&
            content.getCurrentContent().getPlainText());
    };
    const downloadUrl = `${APIBASE}/collection-engine/templates/file${templatesAPI.genLetterUrl(letter.templateId, singleCase.caseId, {
        body: parseText(toHtml(content), mentionSuggestions),
        caseId: singleCase.caseId,
    }, letter.templateFileName || DEFAULT_FILE_NAME)}`;
    return (React.createElement(React.Fragment, null,
        React.createElement(TemplateSearch, { interaction: letter, templateType: TemplateType.LETTER, case: singleCase, customer: props.customer, onChange: (state) => {
                setContent(state);
            } }),
        React.createElement(Input, { disabled: true, placeholder: lang.interactions.contact, value: `${letterTo}${letterAddress && letterAddress.address
                ? ` - ${addressToString(letterAddress)}`
                : ''}` }),
        React.createElement(Editor, { toolbar: { options: EditorToolbarOptions }, editorState: content, onEditorStateChange: (newEditorState) => {
                setContent(newEditorState);
                debounce('letter-content', () => {
                    c.newInteractions.changeEditorState(singleCase.caseId, 'letter', newEditorState);
                }, 1000);
            }, stripPastedStyles: true, mention: {
                separator: ' ',
                trigger: '@',
                suggestions: mentionSuggestions.map((suggestion) => ({
                    value: suggestion.label,
                    text: suggestion.label,
                    url: suggestion.label,
                })),
            } }),
        props.isDebtor && (React.createElement("div", { style: { margin: '1em 0 0 0' } },
            React.createElement(Intent, { intent: letter.intent, editable: true, onEdit: (intent) => {
                    c.newInteractions.changeField(singleCase.caseId, 'letter', 'intent', intent);
                } }))),
        React.createElement(Checkbox, { checked: visibleToClients, onChange: (e) => setVisibleToClients(e.target.checked), placeholder: lang.interactions.visibilityCheckText }),
        React.createElement("br", null),
        React.createElement(Button, { size: "small", loading: letter.loading, disabled: !letter.templateId, onClick: sendLetter }, lang.interactions.sendLetter),
        ' ',
        React.createElement(Button, { size: "small", theme: "secondary", loading: letter.loading, disabled: !letter.templateId || !hasBody(), onClick: toggleLetterPreviewModal }, lang.interactions.preview),
        React.createElement(Button, { size: "small", theme: "secondary", style: { float: 'right', marginRight: '5px' }, onClick: toggleMentionExplanation },
            React.createElement(Info, null)),
        letter.templateId && (React.createElement(React.Fragment, null,
            React.createElement(Button, { style: { marginLeft: '10px' }, onClick: () => {
                    downloadFile(downloadUrl, 'letter.pdf');
                }, size: "small", theme: "secondary" }, lang.interactions.download))),
        letterPreviewModalOpen && (React.createElement(LetterPreview, { onToggle: toggleLetterPreviewModal, downloadUrl: downloadUrl })),
        mentionExplanationModalOpen && (React.createElement(Modal, { onToggle: toggleMentionExplanation, header: lang.interactions.explanation },
            React.createElement(MentionExplanation, { suggestions: mentionSuggestions })))));
};
export default LetterInteraction;
//# sourceMappingURL=LetterInteraction.js.map